"use client";

import { useState, useEffect, useMemo } from "react";
import { Search, BarChart3, Brain, PenLine } from "lucide-react";

const STAGE_DURATION_MS = 3_500;

type LoadingStage = {
   icon: React.ReactNode;
   labelKey: string;
};

const ICON_CLASS = "w-4 h-4 text-primary";

const LOADING_STAGES: LoadingStage[] = [
   { icon: <Search className={ICON_CLASS} />, labelKey: "loadingSearching" },
   { icon: <BarChart3 className={ICON_CLASS} />, labelKey: "loadingAnalyzing" },
   { icon: <Brain className={ICON_CLASS} />, labelKey: "loadingThinking" },
   { icon: <PenLine className={ICON_CLASS} />, labelKey: "loadingComposing" }
];

type ChatLoadingSkeletonProps = {
   hasStreamingContent?: boolean;
   translate: (key: string) => string;
};

export const ChatLoadingSkeleton = ({
   hasStreamingContent = false,
   translate
}: ChatLoadingSkeletonProps) => {
   const [currentStage, setCurrentStage] = useState(0);

   useEffect(() => {
      if (hasStreamingContent) return;

      const interval = setInterval(() => {
         setCurrentStage((prev) => (prev < LOADING_STAGES.length - 1 ? prev + 1 : prev));
      }, STAGE_DURATION_MS);

      return () => clearInterval(interval);
   }, [hasStreamingContent]);

   const stage = useMemo(() => LOADING_STAGES[currentStage], [currentStage]);
   const progressPercent = useMemo(
      () => ((currentStage + 1) / LOADING_STAGES.length) * 100,
      [currentStage]
   );

   if (hasStreamingContent) return null;

   return (
      <div className="csk-root">
         {/* Animated border glow */}
         <div className="csk-glow-border" />

         <div className="csk-inner">
            {/* Top bar: AI avatar + stage + progress */}
            <div className="csk-header">
               <div className="csk-avatar">
                  <div className="csk-avatar-ring" />
                  <div className="csk-avatar-icon">
                     <span>TAI</span>
                     <span>ZEN</span>
                  </div>
               </div>

               <div className="csk-header-content">
                  <div className="csk-stage" key={stage.labelKey}>
                     <span className="csk-stage-icon">{stage.icon}</span>
                     <span className="csk-stage-label">{translate(stage.labelKey)}</span>
                  </div>
               </div>
            </div>

            {/* Progress bar */}
            <div className="csk-progress-track">
               <div
                  className="csk-progress-fill"
                  style={{ width: `${progressPercent}%` }}
               />
               <div
                  className="csk-progress-glow"
                  style={{ left: `${progressPercent}%` }}
               />
            </div>

            {/* Skeleton content area */}
            <div className="csk-content">
               {/* Paragraph skeleton block 1 */}
               <div className="csk-block">
                  <div
                     className="csk-line"
                     style={{ width: "92%", animationDelay: "0s" }}
                  />
                  <div
                     className="csk-line"
                     style={{ width: "80%", animationDelay: "0.1s" }}
                  />
                  <div
                     className="csk-line"
                     style={{ width: "88%", animationDelay: "0.2s" }}
                  />
               </div>

               {/* Paragraph skeleton block 2 */}
               <div className="csk-block">
                  <div
                     className="csk-line"
                     style={{ width: "75%", animationDelay: "0.3s" }}
                  />
                  <div
                     className="csk-line"
                     style={{ width: "95%", animationDelay: "0.4s" }}
                  />
                  <div
                     className="csk-line"
                     style={{ width: "60%", animationDelay: "0.5s" }}
                  />
               </div>

               {/* Final lines */}
               <div className="csk-block">
                  <div
                     className="csk-line"
                     style={{ width: "85%", animationDelay: "0.9s" }}
                  />
                  <div
                     className="csk-line"
                     style={{ width: "40%", animationDelay: "1s" }}
                  />
               </div>
            </div>

            {/* Scanning line effect */}
            <div className="csk-scan-line" />
         </div>

         <style jsx>{`
            .csk-root {
               position: relative;
               width: 100%;
               border-radius: 1rem;
               padding: 2px;
               background: linear-gradient(
                  135deg,
                  hsl(var(--primary) / 0.2),
                  hsl(var(--ring) / 0.15),
                  hsl(var(--primary) / 0.08)
               );
               animation: csk-border-shift 4s ease-in-out infinite;
            }

            .csk-glow-border {
               position: absolute;
               inset: -1px;
               border-radius: 1rem;
               background: conic-gradient(
                  from var(--csk-angle, 0deg),
                  transparent 0%,
                  hsl(var(--primary) / 0.35) 10%,
                  transparent 20%
               );
               animation: csk-rotate 3s linear infinite;
               filter: blur(4px);
               z-index: 0;
            }

            .csk-inner {
               position: relative;
               z-index: 1;
               background: hsl(var(--card) / 0.95);
               border-radius: calc(1rem - 2px);
               padding: 1.25rem;
               overflow: hidden;
               backdrop-filter: blur(12px);
            }

            /* Header */
            .csk-header {
               display: flex;
               align-items: flex-start;
               gap: 0.875rem;
               margin-bottom: 1rem;
            }

            .csk-avatar {
               position: relative;
               width: 1.75rem;
               height: 1.75rem;
               flex-shrink: 0;
               display: flex;
               align-items: center;
               justify-content: center;
            }

            .csk-avatar-ring {
               position: absolute;
               inset: 0;
               border-radius: 50%;
               border: 2px solid transparent;
               border-top-color: hsl(var(--primary));
               border-right-color: hsl(var(--ring));
               animation: csk-spin 1.5s linear infinite;
            }

            .csk-avatar-icon {
               display: flex;
               flex-direction: column;
               align-items: center;
               line-height: 1;
               gap: 1px;
               font-size: 0.4rem;
               font-weight: 800;
               background: linear-gradient(135deg, hsl(var(--primary)), hsl(var(--ring)));
               -webkit-background-clip: text;
               -webkit-text-fill-color: transparent;
               background-clip: text;
               letter-spacing: 0.08em;
            }

            .csk-header-content {
               flex: 1;
               min-width: 0;
            }

            .csk-stage {
               display: flex;
               align-items: center;
               gap: 0.5rem;
               margin-bottom: 0.5rem;
               animation: csk-fade-in 0.4s ease-out;
            }

            .csk-stage-icon {
               display: flex;
               align-items: center;
               animation: csk-pulse 2s ease-in-out infinite;
            }

            .csk-stage-label {
               font-size: 0.8125rem;
               font-weight: 600;
               color: hsl(var(--muted-foreground));
               letter-spacing: 0.02em;
            }

            /* Progress bar */
            .csk-progress-track {
               position: relative;
               height: 3px;
               background: hsl(var(--muted));
               border-radius: 2px;
               margin-bottom: 1.25rem;
               overflow: visible;
            }

            .csk-progress-fill {
               height: 100%;
               border-radius: 2px;
               background: linear-gradient(
                  90deg,
                  hsl(var(--primary)),
                  hsl(var(--ring)),
                  hsl(var(--primary) / 0.7)
               );
               transition: width 0.8s cubic-bezier(0.4, 0, 0.2, 1);
            }

            .csk-progress-glow {
               position: absolute;
               top: -3px;
               width: 8px;
               height: 8px;
               border-radius: 50%;
               background: hsl(var(--primary));
               box-shadow: 0 0 12px hsl(var(--primary) / 0.5);
               transform: translateX(-50%);
               transition: left 0.8s cubic-bezier(0.4, 0, 0.2, 1);
            }

            /* Content skeleton */
            .csk-content {
               display: flex;
               flex-direction: column;
               gap: 1rem;
            }

            .csk-block {
               display: flex;
               flex-direction: column;
               gap: 0.5rem;
            }

            .csk-line {
               height: 0.625rem;
               border-radius: 0.375rem;
               background: linear-gradient(
                  90deg,
                  hsl(var(--border)) 0%,
                  hsl(var(--muted)) 20%,
                  hsl(var(--border)) 40%,
                  hsl(var(--border)) 100%
               );
               background-size: 300% 100%;
               animation: csk-shimmer 2s ease-in-out infinite;
            }

            /* Scan line */
            .csk-scan-line {
               position: absolute;
               top: 0;
               left: 0;
               right: 0;
               height: 1px;
               background: linear-gradient(
                  90deg,
                  transparent,
                  hsl(var(--primary) / 0.35),
                  hsl(var(--ring) / 0.35),
                  transparent
               );
               animation: csk-scan 3s ease-in-out infinite;
               box-shadow: 0 0 8px hsl(var(--primary) / 0.25);
            }

            /* Animations */
            @keyframes csk-shimmer {
               0% {
                  background-position: 300% 0;
               }
               100% {
                  background-position: -300% 0;
               }
            }

            @keyframes csk-rotate {
               0% {
                  --csk-angle: 0deg;
               }
               100% {
                  --csk-angle: 360deg;
               }
            }

            @keyframes csk-spin {
               100% {
                  transform: rotate(360deg);
               }
            }

            @keyframes csk-pulse {
               0%,
               100% {
                  transform: scale(1);
               }
               50% {
                  transform: scale(1.15);
               }
            }

            @keyframes csk-fade-in {
               0% {
                  opacity: 0;
                  transform: translateY(-6px);
               }
               100% {
                  opacity: 1;
                  transform: translateY(0);
               }
            }

            @keyframes csk-scan {
               0% {
                  top: 0;
                  opacity: 0;
               }
               10% {
                  opacity: 1;
               }
               90% {
                  opacity: 1;
               }
               100% {
                  top: 100%;
                  opacity: 0;
               }
            }

            @property --csk-angle {
               syntax: "<angle>";
               initial-value: 0deg;
               inherits: false;
            }
         `}</style>
      </div>
   );
};
